import HomeIcon from '@/assets/svgs/home.svg?react'
import ArrowLeft from '@/assets/svgs/chevron-left.svg?react'
import ArrowRight from '@/assets/svgs/chevron-right.svg?react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { Tooltip } from '../Tooltip/Tooltip'
import { useEllipsisCheck } from '@/hooks/use-ellipsis-check'
import PropTypes from 'prop-types'

const Breadcrumbs = ({ dataTestId = 'breadcrumbs', breadcrumbItems }) => {
  const { isEllipsis, elementRefs } = useEllipsisCheck()

  return (
    <div className="flex flex-wrap items-center justify-start mb-4 gap-2" data-testid={dataTestId}>
      {breadcrumbItems?.map((item, index) => {
        const isCurrentPage = index === breadcrumbItems.length - 1
        const isHomePage = item?.isHomePage ?? false

        const breadcrumbItemClass = 'flex items-center justify-center'

        const breadcrumbHomeClass = clsx('box-border w-6 h-6.25 no-underline', {
          '[&_path]:fill-grey-10': isCurrentPage || isHomePage,
          'border-b border-b-grey-50 [&_path]:fill-grey-50 hover:cursor-pointer':
            !isCurrentPage && !isHomePage,
        })

        const breadcrumbLabelClass = clsx(
          'inline-block box-border align-middle text-ellipsis h-[1.3125rem] text-sm leading-normal no-underline max-tablet-p:overflow-visible max-tablet-p:max-w-none max-tablet-p:h-auto max-tablet-p:whitespace-normal',
          {
            'overflow-visible max-w-none font-extrabold text-grey-10 whitespace-normal':
              isCurrentPage,
            'overflow-hidden whitespace-nowrap max-w-[10.5rem] font-medium text-grey-50 border-b border-b-grey-50 hover:cursor-pointer':
              !isCurrentPage,
          }
        )

        const breadcrumbLeftArrowClass = clsx('hidden justify-center mr-2 [&_path]:fill-grey-50', {
          'max-tablet-p:flex max-tablet-p:items-center': !isCurrentPage,
        })

        const isHomeIconItem = item?.isHomePage !== undefined || item.link === '/'

        return (
          <div
            key={`${index}-${item?.label}`}
            className={breadcrumbItemClass}
            data-testid="breadcrumbs-item"
          >
            {isHomeIconItem && (
              <>
                {isCurrentPage || isHomePage ? (
                  <div className={breadcrumbHomeClass}>
                    <HomeIcon />
                  </div>
                ) : (
                  <Link to={item.link} className={breadcrumbHomeClass}>
                    <HomeIcon />
                  </Link>
                )}
              </>
            )}

            {!isHomeIconItem && (
              <>
                {index !== 0 && (
                  <div className="flex items-center justify-center mr-2 max-tablet-p:rotate-180 [&_path]:fill-grey-50">
                    <ArrowRight />
                  </div>
                )}
                <Tooltip title={item.label} disabled={!isEllipsis[index]} offset={[0, -4]}>
                  {isCurrentPage ? (
                    <div
                      className={breadcrumbLabelClass}
                      ref={el => (elementRefs.current[index] = el)}
                    >
                      {item.label}
                    </div>
                  ) : (
                    <Link
                      to={item.link}
                      state={item.state}
                      className={breadcrumbLabelClass}
                      ref={el => (elementRefs.current[index] = el)}
                    >
                      {item.label}
                    </Link>
                  )}
                </Tooltip>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

Breadcrumbs.propTypes = {
  dataTestId: PropTypes.string,
  breadcrumbItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      link: PropTypes.string,
      isHomePage: PropTypes.bool,
      state: PropTypes.object,
    })
  ).isRequired,
}

export default Breadcrumbs
